import React, { useMemo, useState, useEffect, useCallback } from "react";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Card, CardBody, Button, Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { generateColumns } from "../../../custom/Utils/ColumnUtils";
import useFetchData from "../../../custom/Utils/UseFetchDataUtils";
import usePagination from "../../../custom/Utils/UsePaginationUtils";
import { listPageData, exportPageData } from "./ItemFile";
import {
  downloadFile,
  getRecords,
  uploadExelFile,
} from "../../../services/api.service";
import ModernPopup from "../../../services/popup/Popup";
import { loaderOverlay } from "../../../Style/loaderOverly";
import { LoaderCreatePage } from "../../Loader/Loader";
import ImportModal from "../../../services/popup/ImportModal";

const ItemList = () => {
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [updatedData, setUpdatedData] = useState([]);
  const [dataForExport, setDataForExport] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  /* ---------------- POPUP STATE ---------------- */
  const [popupData, setPopupData] = useState({
    isOpen: false,
    message: "",
    isError: false,
    type: "alert",
    resolver: null,
  });

  const closePopup = () => {
    if (popupData.type === "confirm" && popupData.resolver) {
      popupData.resolver({ isConfirmed: false });
    }
    setPopupData((p) => ({ ...p, isOpen: false, resolver: null }));
  };

  const alertPopup = (message, isError = false) => {
    setPopupData({
      isOpen: true,
      message,
      isError,
      type: "alert",
      resolver: null,
    });
  };

  const confirmPopup = (message) => {
    return new Promise((resolve) => {
      setPopupData({
        isOpen: true,
        message,
        isError: false,
        type: "confirm",
        resolver: resolve,
      });
    });
  };
  const {
    payload,
    navigatePage,
    headerName,
    columnData,
    endpoint,
    searchfields,
    exportFileName,
  } = listPageData();

  const initialPayload = useMemo(() => payload, [reloadKey]);

  /* ---------------- PAGINATION ---------------- */
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
  } = usePagination();

  const { data, isLoading, setSearchKeyword, totalCount } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields
  );

  useEffect(() => {
    if (data) {
      const newArray = data.map((item) => {
        return {
          ...item,
          category_type:
            item?.category?.type === null || item?.category?.type === undefined
              ? "-"
              : item.category?.type === 0
              ? "Material"
              : "Sparepart",
        };
      });
      setUpdatedData(newArray);
    }
  }, [data]);

  // handle create action
  const handleCreate = () => navigate(navigatePage.create);

  // handle edit action
  const handleEdit = useCallback(
    (id) => {
      navigate(navigatePage.edit + "/" + id);
    },
    [navigate, navigatePage.edit]
  );

  const handleView = useCallback(
    (id) => {
      navigate(navigatePage.view + "/" + id);
    },
    [navigate, navigatePage.view]
  );

  // columns data
  const columns = useMemo(
    () =>
      generateColumns(
        updatedData,
        columnData,
        handleEdit,
        handleView,
        page,
        pageSize
      ),
    [updatedData, columnData, handleEdit, handleView, page, pageSize]
  );

  // handle page size change callback
  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount]
  );

  /* ---------------- PAGE META ---------------- */

  /* ---------------- EXPORT ---------------- */
  useEffect(() => {
    const fetchExport = async () => {
      const res = await getRecords(exportPageData, endpoint);
      setDataForExport(res?.data || []);
    };
    fetchExport();
  }, []);

  /* ---------------- IMPORT ---------------- */
  const handleFileSelect = async (e) => {
    setShowImportModal(false);
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      alertPopup("Please select a valid Excel file", true);
      return;
    }

    const confirm = await confirmPopup(
      `Are you sure you want to import "${file.name}"?`
    );

    if (!confirm.isConfirmed) return;

    try {
      setIsLoadingData(true);

      const user = JSON.parse(localStorage.getItem("authUser"));
      const schema = localStorage.getItem("schema") || "";

      const res = await uploadExelFile(file, "/import/materials", {
        useSchemaPayload: false,
        additionalData: { schema, createdBy: user?.id },
      });

      if (res?.code === 1) {
        alertPopup(res.message || "Import successful");

        setReloadKey((k) => k + 1);

        if (typeof res.data === "string" && res.data.includes("<html")) {
          setIsLoadingData(false);
          const viewReport = await confirmPopup(
            "Import completed.\n\nWould you like to view the detailed report?"
          );

          if (viewReport.isConfirmed) {
            const win = window.open("", "_blank");
            win.document.write(res.data);
            win.document.close();
          }
        }
      } else {
        alertPopup(res?.message || "Import failed", true);
      }
    } catch (err) {
      alertPopup(err.message || "Import failed", true);
    } finally {
      setIsLoadingData(false);
      e.target.value = "";
      setShowImportModal(false);
    }
  };

  /* ---------------- EXPORT MAP ---------------- */
  const exportData = dataForExport.map((item, index) => ({
    "Sr. No": index + 1,
    NAME: item?.name || "-",
    STATUS: item?.is_active ? "Active" : "Inactive",
  }));

  /* ---------------- RENDER ---------------- */
  return (
    <>
      <ModernPopup
        isOpen={popupData.isOpen}
        onClose={closePopup}
        message={popupData.message}
        isError={popupData.isError}
        type={popupData.type}
        resolver={popupData.resolver}
      />

      <div className="page-content">
        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={() => document.getElementById("fileInput").click()}
          onDownloadSample={() => {
            const link = document.createElement("a");
            link.href = "/Items_Import_Template.xlsx";
            link.download = "Template.xlsx";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        />

        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumbs title={`${headerName} List`} breadcrumbItems={[]} />

            <Button
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Create {headerName}
            </Button>

            <Button
              color="success"
              className="waves-effect waves-light me-1"
              onClick={() => setShowImportModal(true)}
            >
              Import
            </Button>

            <input
              id="fileInput"
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
            />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={updatedData || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                exportData={exportData}
                exportFileName={exportFileName}
              />
            </CardBody>
          </Card>

          {isLoadingData && (
            <div style={loaderOverlay}>
              <LoaderCreatePage />
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default ItemList;
